import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { AdminerProvider, AuthProvider } from "@marekl/adminer";
import csDict from "@marekl/adminer/i18n/cs";
import { authAdapter } from "./lib/authAdapter";
import { TECH_CONTACT } from "./lib/contacts";
import { MailLink } from "./components/SupportContacts";
import { LinkComponent } from "./lib/LinkComponent";
import { EnvironmentBanner } from "./components/EnvironmentBanner";
import { RequireAuth } from "./auth/RequireAuth";
import { RequireGuest } from "./auth/RequireGuest";
import { PendingTeamGate } from "./auth/PendingTeamGate";
import { AppLayout } from "./shell/AppLayout";
import { RouteError } from "@marekl/adminer/shell";
import { LoginPage } from "./pages/auth/LoginPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { UsersPage } from "./pages/admin/UsersPage";
import { ProfilePage } from "./pages/profile/ProfilePage";
import { AuditLogPage } from "./pages/admin/AuditLogPage";
import { MailOutboxPage } from "./pages/admin/MailOutboxPage";
import {
  TeamRegistrationPage,
  REGISTRATION_FLOW_PATHS,
} from "./pages/public/TeamRegistrationPage";
import { PendingApprovalPage } from "./pages/team/PendingApprovalPage";
import { MembershipPage } from "./pages/team/MembershipPage";
import { TeamDetailPage } from "./pages/team/TeamDetailPage";
import { SeasonRegistrationPage } from "./pages/team/SeasonRegistrationPage";
import { TeamCompetitionsPage } from "./pages/team/TeamCompetitionsPage";
import { TeamCompetitionEntriesPage } from "./pages/team/TeamCompetitionEntriesPage";
import { EntryWizardPage } from "./pages/team/EntryWizardPage";
import { TeamsPage } from "./pages/admin/TeamsPage";
import { FederationsPage } from "./pages/admin/FederationsPage";
import { AccessPage } from "./pages/admin/AccessPage";
import { NoticesPage } from "./pages/admin/NoticesPage";
import { CustomPagesPage } from "./pages/admin/CustomPagesPage";
import { CustomPageEditPage } from "./pages/admin/CustomPageEditPage";
import { CustomPageView } from "./pages/custompages/CustomPageView";
import { MembersRegistryPage } from "./pages/admin/MembersRegistryPage";
import { PersonDuplicatesPage } from "./pages/admin/PersonDuplicatesPage";
import { SeasonsPage } from "./pages/admin/SeasonsPage";
import { SeasonDocumentsPage } from "./pages/admin/SeasonDocumentsPage";
import { SeasonDocumentConsentsPage } from "./pages/admin/SeasonDocumentConsentsPage";
import { RuleSetsPage } from "./pages/admin/RuleSetsPage";
import { CompetitionsPage } from "./pages/admin/CompetitionsPage";
import { CompetitionLayout } from "./shell/CompetitionLayout";
import { CompetitionOverviewPage } from "./pages/competition/CompetitionOverviewPage";
import { CompetitionEntriesPage } from "./pages/competition/CompetitionEntriesPage";
import { EntryTeamPickPage } from "./pages/competition/EntryTeamPickPage";
import { CompetitionSettingsPage } from "./pages/competition/CompetitionSettingsPage";
import { StartListsPage } from "./pages/competition/StartListsPage";
import { CompetitionMusicPage } from "./pages/competition/CompetitionMusicPage";
import { PanelsPage } from "./pages/competition/PanelsPage";
import { DevicesPage } from "./pages/competition/DevicesPage";
import { StartListPage } from "./pages/competition/StartListPage";
import { StartListViewPage } from "./pages/public/StartListViewPage";
import { RuleSetDetailPage } from "./pages/admin/RuleSetDetailPage";
import { DisciplineDetailPage } from "./pages/admin/DisciplineDetailPage";
import {
  useMe,
  hasAnyGlobalRole,
  hasAnyAssignmentRole,
  administersAnyFederation,
  canReadAllCompetitions,
  canReadAllMembers,
} from "./auth/queries";
import { useMyTeams } from "./lib/queries/teams";

function ClenskaZakladnaRoute() {
  const { data: me } = useMe();
  if (canReadAllMembers(me)) {
    return <MembersRegistryPage />;
  }
  return <MembershipPage />;
}

// The duplicate check reads the whole registry, so it has the registry's
// audience; anybody else lands on their own membership page instead.
function MozneDuplicityRoute() {
  const { data: me } = useMe();
  if (hasAnyGlobalRole(me, "admin", "members_admin", "global_viewer")) {
    return <PersonDuplicatesPage />;
  }
  return <MembershipPage />;
}

// The leader's sidebar entry: resolve "my competitions" to the one
// team's page, or fall back to the team list when the leader has
// several (or none).
function SoutezeRoute() {
  const { data: teams = [], isLoading } = useMyTeams();
  if (isLoading) return null;
  if (teams.length === 1) {
    return <Navigate to={`/teams/${teams[0].id}/souteze`} replace />;
  }
  return <Navigate to="/teams" replace />;
}

// The leader's sidebar entry for „Registrace do sezóny": the workspace
// is team-scoped (/teams/:id/registrace) and the sidebar knows no team
// id, so resolve it the same way as SoutezeRoute above.
function RegistraceRoute() {
  const { data: teams = [], isLoading } = useMyTeams();
  if (isLoading) return null;
  if (teams.length === 1) {
    return <Navigate to={`/teams/${teams[0].id}/registrace`} replace />;
  }
  return <Navigate to="/teams" replace />;
}

// The competition list is the door for global managers AND for
// competition-scoped organizers, so a plain roles={} guard is not
// enough — the server filters what each of them actually sees.
function CompetitionsRoute() {
  const { data: me } = useMe();
  if (
    canReadAllCompetitions(me) ||
    hasAnyAssignmentRole(me, "competition_organizer")
  ) {
    return <CompetitionsPage />;
  }
  return <Navigate to="/dashboard" replace />;
}

// The team list is the door for central staff AND for a Správce federace,
// whose scope is one federation rather than a global role. The server
// narrows the rows; this only decides who may open the page.
function TymyRoute() {
  const { data: me } = useMe();
  if (canReadAllMembers(me) || administersAnyFederation(me)) {
    return <TeamsPage />;
  }
  return <Navigate to="/dashboard" replace />;
}

// RootProviders is the router's root element rather than a wrapper around it:
// AdminerProvider's LinkComponent renders <Link>, so it needs router context.
function RootProviders() {
  return (
    <AdminerProvider
      auth={authAdapter}
      i18n={csDict}
      LinkComponent={LinkComponent}
    >
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    </AdminerProvider>
  );
}

// A data router (rather than <Routes>) is what makes useBlocker work, which is
// what lets a page with unsaved changes stop an in-app navigation — see
// lib/useUnsavedChangesGuard. Built at module scope on purpose: constructing it
// inside the component would remount the whole tree on every render and lose
// the blocker's state along with it.
const router = createBrowserRouter([
  {
    element: <RootProviders />,
    // On the ROOT route, so it covers every page. A data router catches a
    // render error inside a route element itself, which is why the
    // <ErrorBoundary> in main.tsx never saw one: the error stops at
    // RouterProvider and, with no errorElement, React Router shows its own
    // developer screen to whoever is using the app. Here the errorElement
    // replaces the whole subtree, <AdminerProvider> included, so the copy is
    // passed in Czech rather than taken from the dictionary.
    errorElement: (
      <RouteError
        homeTo="/dashboard"
        title="Něco se pokazilo"
        subtitle={
          <>
            Stránku se nepodařilo zobrazit. Zkuste ji načíst znovu; pokud se to opakuje,
            napište na <MailLink to={TECH_CONTACT} />, co jste dělali.
          </>
        }
        alertTitle="Podrobnosti pro vývojáře"
        reloadLabel="Načíst znovu"
        homeLabel="Zpět na přehled"
      />
    ),
    children: [
      {
        path: "/login",
        element: (
          <RequireGuest>
            <LoginPage />
          </RequireGuest>
        ),
      },
      // Password reset. Both are RequireGuest for the same reason /login is:
      // somebody already signed in changes their password on ProfilePage.
      //
      // The path of the second one is what the mailed link points at
      // (mail.PasswordResetURL), so the two must change together — with the
      // catch-all redirect below, a mismatch does not 404, it silently
      // bounces to the dashboard and the token is thrown away.
      {
        path: "/obnoveni-hesla",
        element: (
          <RequireGuest>
            <ForgotPasswordPage />
          </RequireGuest>
        ),
      },
      {
        path: "/obnova-hesla",
        element: (
          <RequireGuest>
            <ResetPasswordPage />
          </RequireGuest>
        ),
      },
      // The chooser and one route per flow, all rendering the same page:
      // the flow is read from the path, so each is linkable on its own and
      // the foreign one can stay off the chooser without being unreachable.
      ...["/registrace-tymu", ...REGISTRATION_FLOW_PATHS].map((path) => ({
        path,
        element: (
          <RequireGuest>
            <TeamRegistrationPage />
          </RequireGuest>
        ),
      })),
      {
        path: "/pending-approval",
        element: (
          <RequireAuth>
            <PendingApprovalPage />
          </RequireAuth>
        ),
      },
      {
        element: (
          <RequireAuth>
            <PendingTeamGate>
              <AppLayout />
            </PendingTeamGate>
          </RequireAuth>
        ),
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: "/dashboard", element: <DashboardPage /> },
          {
            path: "/users",
            element: (
              <RequireAuth roles={["admin", "global_viewer"]}>
                <UsersPage />
              </RequireAuth>
            ),
          },
          { path: "/profile", element: <ProfilePage /> },
          {
            path: "/audit-log",
            element: (
              <RequireAuth roles={["admin", "members_admin", "global_viewer"]}>
                <AuditLogPage />
              </RequireAuth>
            ),
          },
          { path: "/admin/tymy", element: <TymyRoute /> },
          {
            path: "/admin/maily",
            element: (
              <RequireAuth roles={["admin", "global_viewer"]}>
                <MailOutboxPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/pristup",
            element: (
              <RequireAuth roles={["admin", "global_viewer"]}>
                <AccessPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/oznameni",
            element: (
              <RequireAuth roles={["admin", "notices_manager", "global_viewer"]}>
                <NoticesPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/stranky",
            element: (
              <RequireAuth roles={["admin", "custom_pages_manager", "global_viewer"]}>
                <CustomPagesPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/stranky/nova",
            element: (
              <RequireAuth roles={["admin", "custom_pages_manager"]}>
                <CustomPageEditPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/stranky/:id",
            element: (
              <RequireAuth roles={["admin", "custom_pages_manager"]}>
                <CustomPageEditPage />
              </RequireAuth>
            ),
          },
          // Any signed-in user may ask; the server answers 404 to anybody
          // the page is not written for.
          { path: "/stranky/:slug", element: <CustomPageView /> },
          {
            path: "/admin/federace",
            element: (
              <RequireAuth roles={["admin", "global_viewer"]}>
                <FederationsPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/sezony",
            element: (
              <RequireAuth roles={["admin", "members_admin", "global_viewer"]}>
                <SeasonsPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/sezony/:id/dokumenty",
            element: (
              <RequireAuth roles={["admin", "members_admin", "global_viewer"]}>
                <SeasonDocumentsPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/sezony/:id/dokumenty/:documentId",
            element: (
              <RequireAuth roles={["admin", "members_admin", "global_viewer"]}>
                <SeasonDocumentConsentsPage />
              </RequireAuth>
            ),
          },
          { path: "/admin/souteze", element: <CompetitionsRoute /> },
          {
            path: "/admin/pravidla",
            element: (
              <RequireAuth roles={["admin", "competitions_admin", "competitions_viewer", "global_viewer"]}>
                <RuleSetsPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/pravidla/:id",
            element: (
              <RequireAuth roles={["admin", "competitions_admin", "competitions_viewer", "global_viewer"]}>
                <RuleSetDetailPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/pravidla/:id/disciplina/:disciplineId",
            element: (
              <RequireAuth roles={["admin", "competitions_admin", "competitions_viewer", "global_viewer"]}>
                <DisciplineDetailPage />
              </RequireAuth>
            ),
          },
          { path: "/clenska-zakladna", element: <ClenskaZakladnaRoute /> },
          { path: "/clenska-zakladna/duplicity", element: <MozneDuplicityRoute /> },
          { path: "/teams", element: <MembershipPage /> },
          { path: "/teams/:id", element: <TeamDetailPage /> },
          {
            path: "/teams/:id/registrace",
            element: <SeasonRegistrationPage />,
          },
          { path: "/registrace-do-sezony", element: <RegistraceRoute /> },
          { path: "/souteze", element: <SoutezeRoute /> },
          { path: "/teams/:id/souteze", element: <TeamCompetitionsPage /> },
          {
            path: "/teams/:id/souteze/:competitionId",
            element: <TeamCompetitionEntriesPage />,
          },
          {
            path: "/teams/:id/souteze/:competitionId/nova-prihlaska",
            element: <EntryWizardPage />,
          },
          {
            path: "/teams/:id/souteze/:competitionId/prihlaska/:entryId",
            element: <EntryWizardPage />,
          },
        ],
      },
      {
        // Režim Soutěž — a sibling shell of AppLayout with its own
        // sidebar; access is checked inside CompetitionLayout.
        element: (
          <RequireAuth>
            <PendingTeamGate>
              <CompetitionLayout />
            </PendingTeamGate>
          </RequireAuth>
        ),
        children: [
          { path: "/soutez/:id", element: <CompetitionOverviewPage /> },
          {
            path: "/soutez/:id/prihlasky",
            element: <CompetitionEntriesPage />,
          },
          {
            path: "/soutez/:id/nova-prihlaska",
            element: <EntryTeamPickPage />,
          },
          { path: "/soutez/:id/kategorie", element: <StartListsPage /> },
          { path: "/soutez/:id/startovni-listina", element: <StartListPage /> },
          { path: "/soutez/:id/hudba", element: <CompetitionMusicPage /> },
          { path: "/soutez/:id/poroty", element: <PanelsPage /> },
          { path: "/soutez/:id/zarizeni", element: <DevicesPage /> },
          {
            path: "/soutez/:id/nastaveni",
            element: <CompetitionSettingsPage />,
          },
        ],
      },
      {
        // The published running order, read-only and deliberately
        // OUTSIDE every shell: printing has to lose the sidebar, the
        // topbar and the buttons, and with no chrome on the page there
        // is nothing to hide. It is the club's view and the printable
        // one at once.
        element: (
          <RequireAuth>
            <Outlet />
          </RequireAuth>
        ),
        children: [
          {
            path: "/soutez/:id/startovni-listina/tisk",
            element: <StartListViewPage />,
          },
        ],
      },
      { path: "*", element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);

export default function App() {
  return (
    <>
      <EnvironmentBanner />
      <RouterProvider router={router} />
    </>
  );
}
